import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  z-index: 1;
  
  a {
    text-decoration: none;
    color: black;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav`
  display: none;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 250px;
  background-color: white;
  padding: 5rem 2rem 2rem;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 2;
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  
  &:hover {
    color: #FF1493;
  }
`;

const ButtonLink = styled(Link)`
  background-color: #FF1493;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  white-space: nowrap;
  
  &:hover {
    color: white;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
  }
`;

const ProfileButton = styled.div`
  margin-left: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 0.5rem;
  }
`;

const SignOutButton = styled.button`
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  font-size: 1rem;
  white-space: nowrap;
  
  &:hover {
    color: #FF1493;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 0.5rem 0;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1.5rem;
  gap: 1rem;
`;

const ProfileImage = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
`;

const ProfilePlaceholder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: #333;
  cursor: pointer;
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 3;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 0;

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const Header = ({ user, isArtist }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase();
    }
    return 'U';
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <Logo>
        <Link to="/">ArtClvb</Link>
      </Logo>
      
      <HamburgerButton onClick={toggleMenu}>
        {isMenuOpen ? '✕' : '☰'}
      </HamburgerButton>

      <Overlay $isOpen={isMenuOpen} onClick={closeMenu} />

      <Nav>
        <ButtonLink to="/">Marketplace</ButtonLink>
        {user && isArtist && (
          <ButtonLink to="/upload">Upload</ButtonLink>
        )}
        
        {user ? (
          <UserMenu>
            <NavLink to="/profile">
              {user.profilePhoto ? (
                <ProfileImage src={user.profilePhoto} alt={user.displayName || 'Profile'} />
              ) : (
                <ProfilePlaceholder>{getInitials()}</ProfilePlaceholder>
              )}
            </NavLink>
            <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
          </UserMenu>
        ) : (
          <>
            <NavLink to="/signin">Sign In</NavLink>
            <NavLink to="/signup">Sign Up</NavLink>
          </>
        )}
      </Nav>

      <MobileNav isOpen={isMenuOpen}>
        {user ? (
          <>
            <NavLink to="/profile" onClick={closeMenu}>
              {user.profilePhoto ? (
                <ProfileImage src={user.profilePhoto} alt={user.displayName || 'Profile'} />
              ) : (
                <ProfilePlaceholder>{getInitials()}</ProfilePlaceholder>
              )}
            </NavLink>
            <SignOutButton onClick={handleSignOut}>Sign Out</SignOutButton>
          </>
        ) : (
          <>
            <NavLink to="/signin" onClick={closeMenu}>Sign In</NavLink>
            <NavLink to="/signup" onClick={closeMenu}>Sign Up</NavLink>
          </>
        )}
        
        <ButtonLink to="/" onClick={closeMenu}>Marketplace</ButtonLink>
        {user && isArtist && (
          <ButtonLink to="/upload" onClick={closeMenu}>Upload</ButtonLink>
        )}
      </MobileNav>
    </HeaderContainer>
  );
};

export default Header;